/**
 * Created by piotr.pozniak@thebeaverhead.com on 07/02/2023.
 *
 * Untitled UI restyle — replaces templates/QueueJobsPageTemplate/index.js.
 * One list page for every queued-job jobType (sync/webhook/ICS
 * update/ICS clean/email), configured per call site instead of each one
 * hand-rolling its own <thead> + picking between two near-identical row
 * components. See pages/Queued*.js for the five thin per-route wrappers.
 *
 * @param {string} title
 * @param {string} jobType
 * @param {string} routeBase - e.g. "/webhook-jobs"; page URLs are `${routeBase}/${page}`
 * @param {boolean=false} hasDuration
 * @param {boolean=false} hasAutoLogin
 * @param {string="type"} iconField - job.data field naming the type/provider icon
 * @param {function(Object): string} [accountLabel] - see columns.js
 * @param {{header: string, render: function(Object): ReactNode}} [secondaryColumn] - see columns.js
 */
import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import Button from "../../theme/untitled/Button/Button";
import Card from "../../theme/untitled/Card/Card";
import DropdownMenu from "../../theme/untitled/DropdownMenu/DropdownMenu";
import Icon from "../../theme/untitled/icons/Icon";
import Pagination from "../../theme/untitled/Pagination/Pagination";
import {
  ToastProvider,
  useToast,
} from "../../theme/untitled/Toast/ToastProvider";
import QueueTabsPageTemplate from "../../templates/QueueTabsPageTemplate/QueueTabsPageTemplate";
import QueueJobsTable from "./QueueJobsTable";
import { accountLabelByPlan, emailColumn } from "./columns";
import { useQueueJobsStore } from "../../../../hooks/redux/queueJobs";
import { useAppStore } from "../../../../hooks/redux/app";
import styles from "./QueueJobsPage.module.scss";

const CLEAN_OPTIONS = [
  { key: "completed", label: "Remove processed", value: "completed" },
  { key: "queued", label: "Remove queued", value: "queued" },
  { key: "all", label: "Remove all", value: "all", destructive: true },
];

// useToast() needs a ToastProvider ancestor, which can't be the same
// component instance that calls the hook — same split as UsersPage.
const QueueJobsListPageContent = ({
  title,
  jobType,
  routeBase,
  hasDuration = false,
  hasAutoLogin = false,
  iconField = "type",
  accountLabel = accountLabelByPlan,
  secondaryColumn = emailColumn,
}) => {
  const match = useRouteMatch();
  const history = useHistory();
  const showToast = useToast();
  const { queueJobs, fetchQueueJobs, clearQueueCollection, deleteQueuedJobs } =
    useQueueJobsStore();
  const { clearErrors } = useAppStore();

  const page = match.params.page ? match.params.page * 1 : 1;

  useEffect(() => {
    clearErrors();
    return () => {
      clearQueueCollection();
      clearErrors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchQueueJobs(page, jobType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (queueJobs.deleteSuccess) {
      showToast({
        variant: "success",
        message: `Removed ${queueJobs.deleteRecordsCount} jobs.`,
      });
      fetchQueueJobs(page, jobType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueJobs.deleteSuccess]);

  const loading = queueJobs.fetch || queueJobs.delete;

  const onRefresh = useCallback(
    () => fetchQueueJobs(page, jobType),
    [fetchQueueJobs, page, jobType]
  );

  const onPaginationPageChange = useCallback(
    (nextPage) => {
      history.push(`${routeBase}/${nextPage}`);
      window.scrollTo({ top: 0 });
    },
    [history, routeBase]
  );

  const onCleanQueue = useCallback(
    (value) => deleteQueuedJobs(jobType, value),
    [deleteQueuedJobs, jobType]
  );

  const cleanMenuItems = useMemo(
    () =>
      CLEAN_OPTIONS.map((option) => ({
        key: option.key,
        label: option.label,
        destructive: option.destructive,
        onClick: () => onCleanQueue(option.value),
      })),
    [onCleanQueue]
  );

  const cleanQueueTrigger = (
    <Button
      hierarchy="secondaryGray"
      size="sm"
      iconTrailing={<Icon name="ChevronDown" />}
      disabled={loading}
    >
      Clean queue
    </Button>
  );

  const actions = (
    <>
      <DropdownMenu
        trigger={cleanQueueTrigger}
        items={cleanMenuItems}
        align="end"
      />
      <Button
        hierarchy="secondaryGray"
        size="sm"
        iconLeading={<Icon name="RefreshCcw01" />}
        onClick={onRefresh}
        loading={loading}
      >
        Refresh
      </Button>
    </>
  );

  const totalCount = queueJobs.pagination?.count || 0;
  const totalPages =
    Math.ceil(totalCount / (queueJobs.pagination?.perPage || 1)) || 0;
  const jobCountLabel = `${totalCount} ${totalCount === 1 ? "job" : "jobs"}`;

  const content = queueJobs.collection.length ? (
    <QueueJobsTable
      jobs={queueJobs.collection}
      rowsDisabled={loading}
      hasDuration={hasDuration}
      hasAutoLogin={hasAutoLogin}
      iconField={iconField}
      accountLabel={accountLabel}
      secondaryColumn={secondaryColumn}
    />
  ) : (
    <div className={styles.empty}>
      <p className={styles.emptyText}>No jobs in this queue.</p>
    </div>
  );

  const paginationEl =
    totalPages > 1 ? (
      <Pagination
        page={queueJobs.pagination.page}
        totalPages={totalPages}
        onPageChange={onPaginationPageChange}
      />
    ) : null;

  return (
    <QueueTabsPageTemplate title={title}>
      <Card>
        <Card.Header>
          <div className={styles.headerActions}>{actions}</div>
        </Card.Header>
        {content}
        <Card.Footer>
          <span className={styles.footerCount}>{jobCountLabel}</span>
          {paginationEl}
        </Card.Footer>
      </Card>
    </QueueTabsPageTemplate>
  );
};

const QueueJobsListPage = (props) => (
  <ToastProvider>
    <QueueJobsListPageContent {...props} />
  </ToastProvider>
);

export default QueueJobsListPage;
